import type { AlertProps } from "@mui/material";

import { Alert, Box, Button } from "@mui/material";

/**
 * ErrorAlert - Display error messages with optional retry action
 *
 * Shows clear error feedback to users and provides option to retry.
 * Useful for query errors and API failures.
 *
 * @example
 * ```tsx
 * const { data, error, refetch } = useQuery(...)
 * if (error) return <ErrorAlert error={error} onRetry={() => refetch()} />
 * ```
 */

type ErrorAlertProps = {
  /**
   * The error object or error message string.
   * Automatically extracts message from Error objects.
   */
  error?: unknown;

  /**
   * Optional callback to retry the failed operation.
   * When provided, shows a retry button.
   */
  onRetry?: () => void;

  /**
   * Text for the retry button.
   * @default 'Retry'
   */
  retryText?: string;

  /**
   * Custom error message to display.
   * If not provided, extracts from error object.
   */
  message?: string;
} & Omit<AlertProps, "children">;

/**
 * ErrorAlert - Display errors with optional retry capability
 *
 * Handles various error types and formats them for display.
 * Shows retry button if onRetry callback provided.
 */
export function ErrorAlert({
  error,
  onRetry,
  retryText = "Retry",
  message,
  ...props
}: ErrorAlertProps) {
  // Extract message from error object if not provided
  const errorMessage = message || getErrorMessage(error);

  return (
    <Alert
      severity="error"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
      {...props}
    >
      <Box>{errorMessage}</Box>
      {onRetry && (
        <Button size="small" onClick={onRetry} sx={{ color: "error.dark", whiteSpace: "nowrap" }}>
          {retryText}
        </Button>
      )}
    </Alert>
  );
}

/**
 * Extract user-friendly error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (!error) return "An error occurred";

  if (typeof error === "string") return error;

  if (error instanceof Error) {
    // Check if it's a GraphQL error with response
    if ("response" in error && typeof error.response === "object" && error.response !== null) {
      const response = error.response as any;
      if (response.errors?.[0]?.message) {
        return response.errors[0].message;
      }
    }

    // Remove redundant "Error: " prefix if present
    return error.message.replace(/^Error:\s*/, "");
  }

  return String(error);
}
